/* BandejaReportesACPage — vista para supervisor_general_pipas + admin.
   Muestra reportes_internos + tickets_areas con acciones:
   - Asignar pipa/chofer/cuándo
   - Marcar atendido
   - Buscar CRC cercanos y fusionar
   - Desligar CRC fusionado
*/
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZONAS = Array.from({ length: 20 }, (_, i) => `Z${i + 1}`);

const CUANDO_OPTS = [
  { v: "HOY",         label: "Hoy",         color: "#DC2626" },
  { v: "MANANA",      label: "Mañana",      color: "#F59E0B" },
  { v: "ESTA_SEMANA", label: "Esta semana", color: "#2563EB" },
];

function fmt(iso) {
  if (!iso) return "—";
  return iso.replace("T", " ").slice(0, 16);
}

function PillEstadoRiego({ valor }) {
  const map = {
    BIEN:        { bg: "#DCFCE7", fg: "#15803D" },
    MAL:         { bg: "#FEF3C7", fg: "#B45309" },
    NO_FUNCIONA: { bg: "#FEE2E2", fg: "#B91C1C" },
  };
  const cfg = map[valor] || { bg: "#E2E8F0", fg: "#475569" };
  return (
    <span style={{
      background: cfg.bg, color: cfg.fg, padding: "2px 8px",
      borderRadius: 999, fontWeight: 700, fontSize: "0.7rem", whiteSpace: "nowrap",
    }}>{valor || "—"}</span>
  );
}

function PillEstadoReporte({ valor }) {
  const map = {
    pendiente: { bg: "#FEE2E2", fg: "#B91C1C", label: "PENDIENTE" },
    asignado:  { bg: "#FEF3C7", fg: "#B45309", label: "ASIGNADO" },
    atendido:  { bg: "#DCFCE7", fg: "#15803D", label: "ATENDIDO" },
  };
  const cfg = map[valor] || { bg: "#E2E8F0", fg: "#475569", label: (valor || "—").toUpperCase() };
  return (
    <span style={{
      background: cfg.bg, color: cfg.fg, padding: "3px 10px",
      borderRadius: 999, fontWeight: 800, fontSize: "0.7rem", whiteSpace: "nowrap",
    }}>{cfg.label}</span>
  );
}

export default function BandejaReportesACPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [estado, setEstado] = useState("pendiente"); // "" all | pendiente | asignado | atendido
  const [zona, setZona] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [abierto, setAbierto] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      await Promise.resolve();
      if (!active) return;
      setLoading(true);
      try {
        const params = {};
        if (estado) params.estado = estado;
        if (zona) params.zona = zona;
        const r = await axios.get(`${API}/reportes-internos`, { params });
        if (active) setItems(r.data.items || []);
      } catch (e) {
        toast.error("No se pudo cargar la bandeja: " + (e.response?.data?.detail || e.message));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [estado, zona]);

  const recargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (estado) params.estado = estado;
      if (zona) params.zona = zona;
      const r = await axios.get(`${API}/reportes-internos`, { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("No se pudo recargar: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const pendientesCount = items.filter(r => r.estado === "pendiente").length;
  const puedeGestionar = ["admin", "supervisor_general_pipas", "supervisor_pipas"].includes(user?.rol);

  return (
    <div className="hub-bg min-h-screen" data-testid="page-bandeja-reportes-ac">
      <header className="topnav">
        <div className="brand">
          <button onClick={() => navigate("/hub")} className="iconbtn mr-1" data-testid="btn-back">
            <i className="fa-solid fa-arrow-left"/>
          </button>
          <div className="logo" style={{ background: "#DC2626" }}>
            <i className="fa-solid fa-triangle-exclamation"/>
          </div>
          <div>
            <div className="brand-title">Bandeja · Reportes de Áreas Contratadas</div>
            <div className="brand-sub">
              Hola, {user?.nombre || user?.usuario} · {puedeGestionar ? "gestión completa" : "solo lectura"}
            </div>
          </div>
        </div>
        <button onClick={logout} data-testid="btn-logout" className="iconbtn">
          <i className="fa-solid fa-right-from-bracket mr-1"/>Salir
        </button>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Filtros */}
        <section className="card-base p-3 flex flex-wrap items-center gap-3" data-testid="filtros">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold uppercase text-slate-500">Estado</label>
            <select
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              data-testid="filtro-estado"
              className="border border-slate-300 rounded-md px-2 py-1.5 text-sm"
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="asignado">Asignado</option>
              <option value="atendido">Atendido</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold uppercase text-slate-500">Zona</label>
            <select
              value={zona}
              onChange={(e) => setZona(e.target.value)}
              data-testid="filtro-zona"
              className="border border-slate-300 rounded-md px-2 py-1.5 text-sm"
            >
              <option value="">Todas</option>
              {ZONAS.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
          <div className="ml-auto text-xs text-slate-600 flex items-center gap-4">
            {loading && <span><i className="fa-solid fa-spinner fa-spin mr-1"/>Cargando…</span>}
            <span>Total: <strong>{items.length}</strong></span>
            {pendientesCount > 0 && (
              <span className="text-red-700"><i className="fa-solid fa-circle-exclamation mr-1"/>{pendientesCount} pendientes</span>
            )}
          </div>
        </section>

        {/* Lista */}
        <section className="card-base p-0 overflow-hidden">
          {items.length === 0 ? (
            <div className="p-12 text-center text-sm text-slate-500">
              <i className="fa-solid fa-inbox text-4xl mb-3 block text-slate-300"/>
              No hay reportes con esos filtros.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {items.map(r => (
                <FilaReporte
                  key={r.id}
                  reporte={r}
                  onClick={() => setAbierto(r)}
                />
              ))}
            </div>
          )}
        </section>

        <div className="text-xs text-slate-500 px-1">
          <i className="fa-solid fa-circle-info mr-1"/>
          Click en un reporte para ver el detalle, asignar pipa y chofer, o fusionar con un reporte ciudadano CRC cercano.
        </div>
      </main>

      {abierto && (
        <DetalleReporteModal
          reporte={abierto}
          puedeGestionar={puedeGestionar}
          onClose={() => setAbierto(null)}
          onActualizado={(r) => {
            setItems(prev => prev.map(x => x.id === r.id ? { ...x, ...r } : x));
            setAbierto(r);
          }}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── */
function FilaReporte({ reporte, onClick }) {
  const r = reporte;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`fila-reporte-${r.id}`}
      className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-start gap-4"
    >
      <div className="flex-shrink-0 w-1 self-stretch rounded"
        style={{ background: r.estado === "pendiente" ? "#DC2626" : r.estado === "asignado" ? "#F59E0B" : "#10B981" }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm">{r.parque_nombre}</span>
          <span className="pill-tiny pill-zone">{r.zona}</span>
          <PillEstadoReporte valor={r.estado}/>
          <PillEstadoRiego valor={r.estado_riego}/>
          {r.crc_folio && (
            <span className="pill-tiny" style={{ background: "#DBEAFE", color: "#1D4ED8", fontWeight: 700 }}>
              <i className="fa-solid fa-link mr-1"/>CRC {r.crc_folio}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
          <span className="mono">{r.id_oficial}</span>
          <span><i className="fa-regular fa-clock mr-1"/>{fmt(r.fecha_reporte)}</span>
          <span><i className="fa-solid fa-user mr-1"/>{r.reportado_por_nombre || r.reportado_por}</span>
          {r.requiere_accion && (
            <span className="text-amber-700"><i className="fa-solid fa-wrench mr-1"/>{r.requiere_accion}</span>
          )}
        </div>
        {r.observaciones && (
          <div className="text-xs text-slate-600 mt-1 italic truncate" title={r.observaciones}>
            &ldquo;{r.observaciones}&rdquo;
          </div>
        )}
      </div>
      {r.foto_url && (
        <div className="flex-shrink-0">
          <img src={r.foto_url} alt="" className="w-16 h-16 object-cover rounded border border-slate-200"/>
        </div>
      )}
      <i className="fa-solid fa-chevron-right text-slate-400 mt-2"/>
    </button>
  );
}

/* ──────────────────────────────────────────────────────────── */
function DetalleReporteModal({ reporte, puedeGestionar, onClose, onActualizado }) {
  const r = reporte;
  const [cuando, setCuando] = useState(r.atencion_cuando || "");
  const [pipa, setPipa] = useState(r.pipa_asignada || "");
  const [chofer, setChofer] = useState(r.chofer_asignado || "");
  const [obsAsig, setObsAsig] = useState(r.observaciones_asignacion || "");
  const [busy, setBusy] = useState(false);
  const [showCRC, setShowCRC] = useState(false);
  const [obsCierre, setObsCierre] = useState("");

  const asignar = async () => {
    if (!cuando || !pipa.trim() || !chofer.trim()) {
      toast.error("Completa cuándo, pipa y chofer");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/reportes-internos/${r.id}/asignar`, {
        atencion_cuando: cuando, pipa_asignada: pipa.trim(), chofer_asignado: chofer.trim(),
        observaciones_asignacion: obsAsig.trim(),
      });
      toast.success("Asignación guardada");
      onActualizado(res.data.reporte);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const atender = async () => {
    if (!window.confirm("¿Marcar este reporte como ATENDIDO?")) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/reportes-internos/${r.id}/atender`, {
        observaciones_cierre: obsCierre.trim(),
      });
      toast.success("Reporte marcado como atendido");
      onActualizado(res.data.reporte);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const desligar = async () => {
    if (!window.confirm("¿Quitar el enlace con el CRC fusionado?")) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/reportes-internos/${r.id}/desligar-crc`);
      toast.success("Reporte desligado del CRC");
      onActualizado(res.data.reporte);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="mp-bg" onClick={onClose} data-testid="modal-detalle-reporte">
      <div
        className="mp-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 760, maxHeight: "92vh" }}
      >
        <header className="mp-head">
          <div>
            <div className="mp-title">
              <i className="fa-solid fa-clipboard-list mr-2"/>Reporte interno · {r.parque_nombre}
            </div>
            <div className="mp-sub">{r.id_oficial} · {r.zona} · {fmt(r.fecha_reporte)}</div>
          </div>
          <button className="mp-close" onClick={onClose} data-testid="btn-cerrar-detalle">
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        <div className="overflow-y-auto" style={{ flex: 1, padding: 0 }}>
          {/* ═══ Bloque CRC fusionado (si existe) ═══ */}
          {r.crc && (
            <section className="px-4 pt-4">
              <div
                className="rounded-lg p-3 border-2"
                style={{ borderColor: "#2563EB", background: "#EFF6FF" }}
                data-testid="bloque-crc"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold text-xs uppercase tracking-wider" style={{ color: "#1D4ED8" }}>
                    <i className="fa-solid fa-bullhorn mr-1.5"/>Reporte ciudadano
                  </div>
                  {puedeGestionar && (
                    <button
                      type="button"
                      onClick={desligar}
                      disabled={busy}
                      className="text-xs text-blue-700 hover:underline"
                      data-testid="btn-desligar-crc"
                    >
                      <i className="fa-solid fa-link-slash mr-1"/>Desligar
                    </button>
                  )}
                </div>
                <dl className="text-sm space-y-1">
                  <Field label="Número CRC" valor={r.crc.folio}/>
                  <Field label="Fecha ciudadano" valor={fmt(r.crc.fecha_ciudadano)}/>
                  <Field label="Descripción" valor={r.crc.descripcion}/>
                  {r.crc.observaciones && <Field label="Observaciones ciudadano" valor={r.crc.observaciones}/>}
                  <Field label="Tipo agua solicitada" valor={r.crc.tipo_agua}/>
                  <Field label="Estatus" valor={r.crc.estatus}/>
                </dl>
              </div>
            </section>
          )}

          {/* ═══ Bloque reporte interno ═══ */}
          <section className="px-4 pt-4">
            <div
              className="rounded-lg p-3 border-2"
              style={{ borderColor: "#DC2626", background: "#FEF2F2" }}
              data-testid="bloque-interno"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-xs uppercase tracking-wider" style={{ color: "#B91C1C" }}>
                  <i className="fa-solid fa-clipboard-list mr-1.5"/>Reporte interno
                </div>
                <PillEstadoReporte valor={r.estado}/>
              </div>
              <dl className="text-sm space-y-1">
                <Field label="Folio interno (ID)" valor={r.id?.slice(0, 8) + "…"}/>
                <Field label="Fecha interno" valor={fmt(r.fecha_reporte)}/>
                <Field label="Supervisor" valor={r.reportado_por_nombre || r.reportado_por}/>
                <Field label="Estado riego" valor={r.estado_riego}/>
                <Field label="Requiere" valor={r.requiere_accion || "—"}/>
                <Field label="Tipo agua" valor={r.tipo_agua}/>
                <Field label="Abastecimiento" valor={r.abastecimiento}/>
                {r.observaciones && <Field label="Observaciones" valor={r.observaciones}/>}
              </dl>
              {r.foto_url && (
                <div className="mt-2">
                  <a href={r.foto_url} target="_blank" rel="noreferrer">
                    <img src={r.foto_url} alt="Evidencia" className="rounded border border-slate-200 max-h-64"/>
                  </a>
                </div>
              )}
            </div>
          </section>

          {/* ═══ Acciones (solo gestión) ═══ */}
          {puedeGestionar && r.estado !== "atendido" && (
            <section className="px-4 pt-4">
              <div className="rounded-lg p-3 border border-slate-300 bg-white">
                <div className="font-bold text-xs uppercase tracking-wider text-slate-700 mb-2">
                  <i className="fa-solid fa-truck-droplet mr-1.5"/>Asignación
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">¿Cuándo se atiende?</label>
                    <select
                      value={cuando}
                      onChange={(e) => setCuando(e.target.value)}
                      data-testid="select-cuando"
                      className="border border-slate-300 rounded-md px-2 py-2 text-sm w-full"
                    >
                      <option value="">— Selecciona —</option>
                      {CUANDO_OPTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Pipa a enviar</label>
                    <input
                      value={pipa} onChange={(e) => setPipa(e.target.value)}
                      placeholder="Ej. 309, 326…"
                      data-testid="input-pipa"
                      className="border border-slate-300 rounded-md px-2 py-2 text-sm w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Chofer asignado</label>
                    <input
                      value={chofer} onChange={(e) => setChofer(e.target.value)}
                      placeholder="Ej. Oscar Acosta"
                      data-testid="input-chofer"
                      className="border border-slate-300 rounded-md px-2 py-2 text-sm w-full"
                    />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="text-xs text-slate-500 block mb-1">Notas (opcional)</label>
                  <input
                    value={obsAsig} onChange={(e) => setObsAsig(e.target.value)}
                    placeholder="Ej. Pasa mañana en la mañana"
                    data-testid="input-obs-asig"
                    className="border border-slate-300 rounded-md px-2 py-2 text-sm w-full"
                  />
                </div>
                <button
                  type="button"
                  onClick={asignar}
                  disabled={busy}
                  data-testid="btn-asignar"
                  className="btn-primary"
                  style={{ background: "#1D4ED8" }}
                >
                  <i className="fa-solid fa-floppy-disk mr-1.5"/>
                  {r.atencion_cuando ? "Actualizar asignación" : "Guardar asignación"}
                </button>

                {!r.crc && (
                  <button
                    type="button"
                    onClick={() => setShowCRC(true)}
                    disabled={busy}
                    data-testid="btn-ver-crc-cercanos"
                    className="ml-2 btn-primary"
                    style={{ background: "#0EA5E9" }}
                  >
                    <i className="fa-solid fa-link mr-1.5"/>Buscar CRC cercanos
                  </button>
                )}
              </div>
            </section>
          )}

          {/* ═══ Atender ═══ */}
          {puedeGestionar && r.estado === "asignado" && (
            <section className="px-4 pt-4 pb-4">
              <div className="rounded-lg p-3 border border-green-300 bg-green-50">
                <div className="font-bold text-xs uppercase tracking-wider text-green-800 mb-2">
                  <i className="fa-solid fa-circle-check mr-1.5"/>Cierre
                </div>
                <label className="text-xs text-slate-500 block mb-1">Observaciones de cierre (opcional)</label>
                <textarea
                  value={obsCierre}
                  onChange={(e) => setObsCierre(e.target.value)}
                  rows={2}
                  data-testid="input-obs-cierre"
                  className="border border-slate-300 rounded-md px-2 py-2 text-sm w-full mb-2"
                />
                <button
                  type="button"
                  onClick={atender}
                  disabled={busy}
                  data-testid="btn-marcar-atendido"
                  className="btn-primary"
                  style={{ background: "#16A34A" }}
                >
                  <i className="fa-solid fa-check mr-1.5"/>Marcar como atendido
                </button>
              </div>
            </section>
          )}

          {/* ═══ Atendido — info ═══ */}
          {r.estado === "atendido" && (
            <section className="px-4 pt-4 pb-4">
              <div className="rounded-lg p-3 bg-green-100 border border-green-300 text-sm text-green-800">
                <i className="fa-solid fa-circle-check mr-1.5"/>
                <strong>Atendido</strong> por {r.atendido_por_nombre || r.atendido_por} el {fmt(r.atendido_en)}
                {r.observaciones_cierre && <div className="italic mt-1">&ldquo;{r.observaciones_cierre}&rdquo;</div>}
              </div>
            </section>
          )}

          <div className="h-4"/>
        </div>
      </div>

      {showCRC && (
        <SubModalCRCCercanos
          reporteId={r.id}
          onClose={() => setShowCRC(false)}
          onFusionado={(reporteActualizado) => {
            setShowCRC(false);
            onActualizado(reporteActualizado);
          }}
        />
      )}
    </div>
  );
}

function Field({ label, valor }) {
  return (
    <div className="flex flex-wrap gap-2 text-sm">
      <span className="text-xs font-bold uppercase text-slate-500" style={{ minWidth: 130 }}>{label}:</span>
      <span className="text-slate-800">{valor || "—"}</span>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── */
function SubModalCRCCercanos({ reporteId, onClose, onFusionado }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    axios.get(`${API}/reportes-internos/${reporteId}/crc-cercanos`)
      .then(r => setItems(r.data.items || []))
      .catch(e => toast.error("Error: " + (e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [reporteId]);

  const fusionar = async (crc) => {
    if (!window.confirm(`¿Fusionar este reporte con el CRC ${crc.folio}?`)) return;
    setBusyId(crc.id);
    try {
      const res = await axios.post(`${API}/reportes-internos/${reporteId}/fusionar-crc`, {
        crc_solicitud_id: crc.id,
      });
      toast.success("Fusionado con éxito");
      onFusionado(res.data.reporte);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusyId(""); }
  };

  return (
    <div
      className="mp-bg"
      onClick={onClose}
      data-testid="modal-crc-cercanos"
      style={{ zIndex: 95 }}
    >
      <div
        className="mp-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}
      >
        <header className="mp-head">
          <div>
            <div className="mp-title">
              <i className="fa-solid fa-map-location-dot mr-2"/>CRC cercanos al parque
            </div>
            <div className="mp-sub">Radio 150 m · misma zona · activos (no completados)</div>
          </div>
          <button className="mp-close" onClick={onClose} data-testid="btn-cerrar-crc">
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>
        <div className="overflow-y-auto" style={{ maxHeight: 420 }}>
          {loading ? (
            <div className="p-6 text-center text-sm text-slate-500">
              <i className="fa-solid fa-spinner fa-spin mr-1"/>Buscando…
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <i className="fa-solid fa-circle-info text-2xl mb-2 block text-slate-300"/>
              No se encontraron CRC activos cerca de este parque.<br/>
              <span className="text-xs">Puedes intentar más tarde o pedir a Yanett que capture el CRC primero.</span>
            </div>
          ) : (
            <ul>
              {items.map(c => (
                <li
                  key={c.id}
                  className="px-4 py-3 border-b border-slate-100"
                  data-testid={`crc-opcion-${c.folio}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm">
                        {c.folio} <span className="text-xs text-slate-400">({c.estatus})</span>
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">{c.direccion_texto}</div>
                      <div className="text-xs text-slate-500 mt-1 flex gap-3">
                        <span><i className="fa-solid fa-ruler mr-1"/>{c.distancia_m} m</span>
                        <span><i className="fa-regular fa-clock mr-1"/>{fmt(c.creado_en)}</span>
                        {c.tipo_agua && <span><i className="fa-solid fa-droplet mr-1"/>{c.tipo_agua}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => fusionar(c)}
                      disabled={busyId === c.id}
                      data-testid={`btn-fusionar-${c.folio}`}
                      className="btn-primary"
                      style={{ background: "#2563EB", whiteSpace: "nowrap" }}
                    >
                      <i className="fa-solid fa-link mr-1"/>Fusionar
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
